import React, { useEffect, useRef } from "react";
import { FALLBACK_WEAR_COST_PER_KM_CHF } from "../../constants/pricing";
import { fetchPriceConfiguration } from "../../services/PriceConfigurationApi";
import { pricingPrefsSlice, usePersistedSlice, usePersistenceReady } from "../persistence";
import { useEphemeralSliceSetter } from "./hooks/useEphemeralSliceSetter";
import { exchangeRateSessionSlice } from "./slices/exchangeRateSession.slice";

/**
 * After persisted state is ready, loads CHF→EUR and optionally aligns factory-default wear with the server.
 */
export const PricingBootstrap: React.FC = () => {
    const [ pricingPrefs, setPricingPrefs ] = usePersistedSlice( pricingPrefsSlice );
    const storageReady = usePersistenceReady();
    const setExchangeRate = useEphemeralSliceSetter( exchangeRateSessionSlice );
    const wearRef = useRef( pricingPrefs.wearCostPerKmChf );

    useEffect( () => {
        wearRef.current = pricingPrefs.wearCostPerKmChf;
    }, [ pricingPrefs.wearCostPerKmChf ] );

    useEffect( () => {
        if ( storageReady !== true ) {
            return;
        }

        let cancelled = false;

        const run = async() => {
            const cfg = await fetchPriceConfiguration();
            if ( cancelled ) {
                return;
            }

            setExchangeRate( { "chfToEurRate": cfg.chfToEurRate } );

            const currentWear = wearRef.current;
            if ( currentWear === FALLBACK_WEAR_COST_PER_KM_CHF &&
              cfg.defaultWearCostPerKmChf !== FALLBACK_WEAR_COST_PER_KM_CHF ) {
                setPricingPrefs( ( previous ) => ( {
                    ...previous,
                    "wearCostPerKmChf": cfg.defaultWearCostPerKmChf,
                } ) );
            }
        };

        void run();

        return () => {
            cancelled = true;
        };
    }, [ setExchangeRate, setPricingPrefs, storageReady ] );

    return null;
};
